import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import NewsletterSignupForm from './NewsletterSignupForm';

const API_URL = "";

// Default academy info for initial render
const defaultAcademyInfo = {
  name: "The Coppini Academy of Fine Art",
  tagline: "Furthering the Interest in Classic, Representational Art Since 1945",
  contact: {
    address: "115 Melrose Place, San Antonio, TX 78212",
    phone: "(210) 530-8445",
    email: "info@thecoppini.org"
  }
};

export const Footer = () => {
  const [academyInfo, setAcademyInfo] = useState(defaultAcademyInfo);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/academy-info`);
        if (res.ok) {
          setAcademyInfo(await res.json());
        }
      } catch (err) {
        console.error('Error fetching academy info:', err);
      }
    };
    fetchInfo();
  }, []);

  return (
    <footer className="bg-stone-900 text-stone-300">
      {/* Newsletter Section */}
      <div className="border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-xl font-serif text-white mb-2">Stay Connected</h3>
              <p className="text-sm text-stone-400">
                Get updates on classes, workshops, and events from CAFA
              </p>
            </div>
            <div className="w-full md:w-auto md:min-w-[400px]">
              <NewsletterSignupForm compact className="w-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About */}
          <div className="md:col-span-2">
            <h3 className="text-xl font-serif text-white mb-4">
              {academyInfo.name}
            </h3>
            <p className="text-sm leading-relaxed mb-4">
              {academyInfo.tagline}
            </p>
            <p className="text-sm text-stone-400">
              Founded in 1945. A 501(c)(3) nonprofit since 1952.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="hover:text-cafa-blue-300 transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/classes" className="hover:text-cafa-blue-300 transition-colors">
                  Classes
                </Link>
              </li>
              <li>
                <Link to="/calendar" className="hover:text-cafa-blue-300 transition-colors">
                  Calendar
                </Link>
              </li>
              <li>
                <Link to="/workshops" className="hover:text-cafa-blue-300 transition-colors">
                  Workshops
                </Link>
              </li>
              <li>
                <Link to="/membership" className="hover:text-cafa-blue-300 transition-colors">
                  Membership
                </Link>
              </li>
            </ul>
            {/* SEO Links */}
            <h4 className="text-white font-semibold mt-6 mb-3">Art Education</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/painting-classes-san-antonio" className="hover:text-cafa-blue-300 transition-colors">
                  Painting Classes
                </Link>
              </li>
              <li>
                <Link to="/drawing-classes-san-antonio" className="hover:text-cafa-blue-300 transition-colors">
                  Drawing Classes
                </Link>
              </li>
              <li>
                <Link to="/kids-art-classes-san-antonio" className="hover:text-cafa-blue-300 transition-colors">
                  Kids Art Classes
                </Link>
              </li>
              <li>
                <Link to="/classes/kids" className="hover:text-cafa-blue-300 transition-colors">
                  Youth Programs
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start space-x-2">
                <MapPin size={16} className="mt-1 flex-shrink-0 text-cafa-blue-300" />
                <span>{academyInfo.contact.address}</span>
              </li>
              <li className="flex items-center space-x-2">
                <Phone size={16} className="flex-shrink-0 text-cafa-blue-300" />
                <span>{academyInfo.contact.phone}</span>
              </li>
              <li className="flex items-center space-x-2">
                <Mail size={16} className="flex-shrink-0 text-cafa-blue-300" />
                <a href="mailto:info@thecoppini.org" className="hover:text-cafa-blue-300 transition-colors">info@thecoppini.org</a>
              </li>
            </ul>
            <div className="flex space-x-4 mt-6">
              <a href="https://www.facebook.com/CoppiniAcademyofFineArt/" target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-cafa-blue-300 transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="https://www.instagram.com/thecoppiniacademyoffineart/" target="_blank" rel="noopener noreferrer" className="text-stone-300 hover:text-cafa-blue-300 transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-stone-800 mt-8 pt-8 text-sm text-center text-stone-400">
          <p>&copy; {new Date().getFullYear()} The Coppini Academy of Fine Art. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};
